import React, { createContext, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { matchesCombo, parseCombo } from 'utils/platform';
import { isEditableTarget, isMultilineEditorTarget } from 'utils/domTarget';

// Global keyboard shortcuts, with scoping: any overlay (modal/drawer) can
// push its own scope on top of the stack while it's visible so its
// shortcuts take priority over whatever is behind it (e.g. a vulnerability
// detail drawer's tabs vs. the table's own tabs). Only the top-most scope
// that declares a matching combo handles the key event - no bubbling.
export const ShortcutContext = createContext(null);

const GLOBAL_SCOPE = 'global';

// Arrow keys are reserved for text navigation inside editable fields
// regardless of modifiers - mod+ArrowLeft/Right in particular is the
// near-universal OS convention for "jump a word" while editing text, so a
// shortcut like tab navigation must never steal it from a focused input.
const TEXT_NAVIGATION_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

// A single-key combo without a modifier shouldn't hijack typing either
// (e.g. a future "[" shortcut while the user is filling a search box), but
// Escape must keep working everywhere, matching the app's existing
// convention.
//
// Enter gets the same narrower exemption the old useKeyPress hook gave it:
// it only hijacks a multiline editor (textarea/contentEditable, where Enter
// must insert a newline), not a plain single-line input/select - that's the
// "submit the form" convention several modals rely on (see useFormShortcuts'
// enterToSave), so a single-line field must not swallow it.
const shouldSkipInEditableTarget = (combo, target) => {
  const { key, needsMod, needsAlt } = parseCombo(combo);
  if (combo === 'Escape') return false;
  if (TEXT_NAVIGATION_KEYS.includes(key)) return isEditableTarget(target);
  if (needsMod || needsAlt) return false;
  if (key === 'Enter') return isMultilineEditorTarget(target);

  return isEditableTarget(target);
};

export const ShortcutProvider = ({ children }) => {
  // Imperative refs, not state: keydown resolution doesn't need re-renders.
  const scopeStackRef = useRef([]);
  const handlersRef = useRef(new Map());

  const getScopeHandlers = (scopeId) => {
    if (!handlersRef.current.has(scopeId)) handlersRef.current.set(scopeId, new Map());
    return handlersRef.current.get(scopeId);
  };

  // Each combo maps handler -> description, so unregistering by handler
  // stays trivial while still keeping a label around for the help panel
  // (Common/Components/ShortcutsHelp, opened with mod+/).
  const registerHandler = useCallback((scopeId, combo, handler, description) => {
    const scopeHandlers = getScopeHandlers(scopeId);
    if (!scopeHandlers.has(combo)) scopeHandlers.set(combo, new Map());
    scopeHandlers.get(combo).set(handler, description);

    return () => {
      scopeHandlers.get(combo)?.delete(handler);
      if (scopeHandlers.get(combo)?.size === 0) scopeHandlers.delete(combo);
    };
  }, []);

  const pushScope = useCallback((scopeId) => {
    scopeStackRef.current = [...scopeStackRef.current, scopeId];
  }, []);

  const popScope = useCallback((scopeId) => {
    const index = scopeStackRef.current.lastIndexOf(scopeId);
    if (index === -1) return;
    scopeStackRef.current = [
      ...scopeStackRef.current.slice(0, index),
      ...scopeStackRef.current.slice(index + 1)
    ];
  }, []);

  const runMatchingHandler = (scopeHandlers, event) => {
    for (const [combo, handlers] of scopeHandlers.entries()) {
      if (!matchesCombo(event, combo)) continue;
      if (shouldSkipInEditableTarget(combo, event.target)) continue;

      event.preventDefault();
      handlers.forEach((description, handler) => handler(event));
      return true;
    }
    return false;
  };

  const trigger = useCallback((combo) => {
    const orderedScopes = [...scopeStackRef.current].reverse().concat(GLOBAL_SCOPE);

    for (const scopeId of orderedScopes) {
      const handlers = handlersRef.current.get(scopeId)?.get(combo);
      if (!handlers || handlers.size === 0) continue;

      handlers.forEach((description, handler) => handler());
      return true;
    }
    return false;
  }, []);

  const handleKeyDown = useCallback((event) => {
    const orderedScopes = [...scopeStackRef.current].reverse();

    for (const scopeId of orderedScopes) {
      const scopeHandlers = handlersRef.current.get(scopeId);
      if (scopeHandlers && runMatchingHandler(scopeHandlers, event)) return;
    }

    const globalHandlers = handlersRef.current.get(GLOBAL_SCOPE);
    if (globalHandlers) runMatchingHandler(globalHandlers, event);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Snapshot of what would actually fire right now: walks the same
  // top-to-bottom order handleKeyDown uses, keeping only the first
  // (highest-priority) registration per combo - a combo declared again in
  // a lower scope is shadowed and wouldn't fire, so it's left out. Powers
  // Common/Components/ShortcutsHelp.
  const getActiveShortcuts = useCallback(() => {
    const orderedScopeIds = [...scopeStackRef.current].reverse().concat(GLOBAL_SCOPE);
    const seenCombos = new Set();
    const shortcuts = [];

    orderedScopeIds.forEach((scopeId) => {
      const scopeHandlers = handlersRef.current.get(scopeId);
      if (!scopeHandlers) return;

      scopeHandlers.forEach((handlers, combo) => {
        if (seenCombos.has(combo)) return;
        seenCombos.add(combo);

        const description = [...handlers.values()].find(Boolean);
        if (description) shortcuts.push({ combo, description, isGlobal: scopeId === GLOBAL_SCOPE });
      });
    });

    return shortcuts;
  }, []);

  return (
    <ShortcutContext.Provider value={ { registerHandler, pushScope, popScope, getActiveShortcuts, trigger } }>
      {children}
    </ShortcutContext.Provider>
  );
};

ShortcutProvider.propTypes = {
  children: PropTypes.node.isRequired
};
